import React from "react";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import useScrollToTop from "../../../../hooks/useScollToTop";

import { useTechniquesDashboard } from "./hooks/useTechniquesDashboard";

import TechniquesDashboardHeader from "./components/TechniquesDashboardHeader";
import TechniquesFiltersCard from "./components/TechniquesFiltersCard";
import TechniquesKpiCards from "./components/TechniquesKpiCards";
import TechniquesGrid from "./components/TechniquesGrid";
import SkeletonCard from "./components/SkeletonCard";
import TechniquesEmptyState from "./components/TechniquesEmptyState";
import TechniqueDrawer from "./components/TechniqueDrawer";

export default function TechniquesUserDashboard() {
  useScrollToTop();

  const {
    allTechniques,
    filteredTechniques,
    visibleTechniques,
    dynamicOptions,
    stats,

    loading,
    favorites,
    userId,

    ui,
    updateUi,
    debouncedSearch,
    setDebouncedSearch,

    openedTechnique,
    openTechnique,
    closeTechnique,

    toggleFavorite,
    resetFilters,
    loadMore,

    copyShareLink,
    shareCopied,
  } = useTechniquesDashboard();

  const hasMore = visibleTechniques.length < filteredTechniques.length;

  return (
    <section className="w-full pb-10 pt-5 font-satoshi">
      <div className="mx-auto max-w-[1900px] space-y-5 px-3 sm:px-4 lg:px-6">
        <TechniquesDashboardHeader />

        <TechniquesFiltersCard
          ui={ui}
          dynamicOptions={dynamicOptions}
          debouncedSearch={debouncedSearch}
          setDebouncedSearch={setDebouncedSearch}
          onChange={updateUi}
          onReset={resetFilters}
          onShare={copyShareLink}
          shareCopied={shareCopied}
          loading={loading}
          resultCount={filteredTechniques.length}
          totalCount={allTechniques.length}
          favoriteCount={favorites.length}
          hasUser={!!userId}
        />

        <TechniquesKpiCards stats={stats} loading={loading} />

        <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

          <div className="p-4 sm:p-5 lg:p-6">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
                  Bibliothèque technique
                </p>

                <h2 className="mt-1 text-xl font-bold text-slate-900 dark:text-white">
                  Techniques disponibles
                </h2>

                <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">
                  Consultez les techniques par ceinture, famille, fiche UV2 ou
                  favoris. Cliquez sur une carte pour ouvrir la fiche dans un
                  drawer.
                </p>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm dark:border-slate-700 dark:bg-slate-800/60">
                <span className="text-slate-500 dark:text-slate-400">
                  Résultats :
                </span>{" "}
                <span className="font-bold text-slate-900 dark:text-white">
                  {filteredTechniques.length}
                </span>
                <span className="text-slate-500 dark:text-slate-400">
                  {" "}
                  / {allTechniques.length}
                </span>
              </div>
            </div>

            <div className="mt-5">
              {loading ? (
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
                  {Array.from({ length: 12 }).map((_, index) => (
                    <SkeletonCard key={index} />
                  ))}
                </div>
              ) : filteredTechniques.length === 0 ? (
                <TechniquesEmptyState onReset={resetFilters} />
              ) : (
                <TechniquesGrid
                  techniques={visibleTechniques}
                  favorites={favorites}
                  hasMore={hasMore}
                  total={filteredTechniques.length}
                  onLoadMore={loadMore}
                  onOpen={openTechnique}
                  onToggleFavorite={toggleFavorite}
                  userId={userId}
                />
              )}
            </div>
          </div>
        </section>
      </div>

      <TechniqueDrawer
        isOpen={!!openedTechnique}
        technique={openedTechnique}
        isFavorite={
          openedTechnique ? favorites.includes(openedTechnique.id) : false
        }
        canFavorite={!!userId}
        onClose={closeTechnique}
        onToggleFavorite={() =>
          openedTechnique ? toggleFavorite(openedTechnique.id) : null
        }
      />

      <ToastContainer />
    </section>
  );
}
